import {useDebugValue} from 'react'
import {useSelector} from 'react-redux'
import {HotelOfferEntity} from 'modules/sapiSearch/slice'
import {getIsNoDatesSearch} from 'modules/search/selectors'
import {getTopOffersParameters} from 'modules/searchApi/selectors'
import {RootState} from 'store'

import {selectIsLoading} from '@vio/core/src/auth/modules/selectors'
import {
  GetOffersParams,
  OffersState,
  useGetOffersQuery
} from '@vio/core/src/sapi/services/searchApi'
import {SearchParameters} from '@vio/core/src/sapi/types'

interface ReturnValue {
  offerEntity?: HotelOfferEntity
  isComplete: boolean
  isError: boolean
  /**
   * True as soon as the slice holds at least one offer for this hotel and
   * we are not in the middle of a refetch. Lets early consumers (e.g. the
   * V3 PriceInsightsBanner) render with the search-anchor offer before SAPI
   * reports `Completed`. The `!isFetching` guard prevents reading stale
   * offers from a previous params round (sapiSearch retains the prior
   * `offerEntity` until the new fetch resolves).
   */
  hasAnyOffers: boolean
  searchedParameters: GetOffersParams | SearchParameters
}

/**
 * Provide offers for the AP Offers component
 */
export const useAccommodationTopOffers = ({hotelId}): ReturnValue => {
  const isAuthLoading = useSelector(selectIsLoading)
  const searchParameters = useSelector((state: RootState) =>
    getTopOffersParameters(state, hotelId)
  )
  const isNoDatesSearch = useSelector(getIsNoDatesSearch)

  const value = useGetOffersQuery(searchParameters, {
    skip: !hotelId || isAuthLoading || isNoDatesSearch,
    selectFromResult: result => ({
      offerEntity: result.data?.offerEntity,
      searchedParameters: result.originalArgs,
      isComplete:
        result.data?.status === OffersState.Completed &&
        !result.isFetching &&
        !result.isLoading &&
        !result.isUninitialized,
      isError: result.isError,
      hasAnyOffers:
        (result.data?.offerEntity?.offers?.length ?? 0) > 0 &&
        !result.isFetching
    })
  })

  useDebugValue(value)

  return value
}
